import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { CheckCircle2, Clock } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { articleSchema, breadcrumbSchema, localizedHead, medicalWebPageSchema, validateLangSearch } from "@/lib/seo";
import { RelatedLinks } from "@/components/related-links";
import { ConsultCta } from "@/components/consult-cta";

export const Route = createFileRoute("/studien")({
  validateSearch: validateLangSearch,
  loaderDeps: ({ search }) => ({ lang: search.lang }),
  loader: ({ deps }) => ({ lang: deps.lang }),
  head: ({ loaderData }) => ({
    ...localizedHead("/studien", loaderData?.lang),
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(breadcrumbSchema("/studien", loaderData?.lang)) },
      { type: "application/ld+json", children: JSON.stringify(medicalWebPageSchema("/studien", loaderData?.lang, { specialty: "Endocrine", aspect: "Treatment" })) },
      { type: "application/ld+json", children: JSON.stringify(articleSchema("/studien", loaderData?.lang, { section: "Clinical trials" })) },
    ],
  }),
  component: Page,
});



function Page() {
  const { t } = useI18n();
  const generations = [
    {
      gen: "Generation 1",
      name: "Semaglutid",
      brand: "Wegovy / Ozempic",
      type: t.studies.typeMono,
      loss: "~15 %",
      lossValue: 62,
      status: t.studies.approved,
      statusType: "approved" as const,
    },
    {
      gen: "Generation 2",
      name: "Tirzepatid",
      brand: "Mounjaro / Zepbound",
      type: t.studies.typeDual,
      loss: "~20–22 %",
      lossValue: 88,
      status: t.studies.approved,
      statusType: "approved" as const,
    },
    {
      gen: "Generation 3",
      name: "Retatrutid",
      brand: t.studies.develop,
      type: t.studies.typeTriple,
      loss: "~24 %",
      lossValue: 100,
      status: t.studies.notApproved,
      statusType: "study" as const,
    },
  ];

  return (
    <div className="mx-auto max-w-6xl px-4 py-12 md:py-20">
      <div className="max-w-3xl">
        <p className="text-xs font-semibold uppercase tracking-widest text-indigo">
          {t.studies.eyebrow}
        </p>
        <h1 className="mt-3 font-display text-4xl leading-tight text-foreground md:text-5xl">
          {t.studies.h1}
        </h1>
        <p className="mt-4 text-lg text-muted-foreground">{t.studies.lead}</p>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6 }}
        className="mt-10 grid gap-4 md:grid-cols-3"
      >
        <div className="card-elevated col-span-1 flex flex-col justify-between overflow-hidden bg-gradient-to-br from-emerald/10 to-transparent p-8 md:col-span-2">
          <span className="text-xs font-semibold uppercase tracking-widest text-emerald">
            {t.studies.headline}
          </span>
          <div className="mt-4">
            <p className="font-display text-7xl leading-none text-foreground md:text-8xl">
              -24<span className="text-4xl text-muted-foreground">%</span>
            </p>
            <p className="mt-3 text-base text-muted-foreground md:text-lg">
              {t.studies.headlineCaption}
            </p>
          </div>
        </div>
        <div className="grid gap-4">
          <div className="card-elevated p-5">
            <Clock className="h-5 w-5 text-indigo" />
            <p className="mt-3 font-display text-3xl text-foreground">48 {t.studies.weeks}</p>
            <p className="text-sm text-muted-foreground">{t.studies.duration}</p>
          </div>
          <div className="card-elevated p-5">
            <CheckCircle2 className="h-5 w-5 text-emerald" />
            <p className="mt-3 font-display text-3xl text-foreground">Phase III</p>
            <p className="text-sm text-muted-foreground">{t.studies.phase}</p>
          </div>
        </div>
      </motion.div>

      <section className="mt-16">
        <h2 className="font-display text-3xl text-foreground">{t.studies.compareTitle}</h2>
        <p className="mt-2 text-muted-foreground">{t.studies.compareLead}</p>

        <div className="mt-6 hidden overflow-hidden rounded-2xl border border-border md:block">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-5 py-4">{t.studies.th.gen}</th>
                <th className="px-5 py-4">{t.studies.th.drug}</th>
                <th className="px-5 py-4">{t.studies.th.type}</th>
                <th className="px-5 py-4">{t.studies.th.loss}</th>
                <th className="px-5 py-4">{t.studies.th.status}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {generations.map((g, i) => (
                <tr key={g.name} className={i === 2 ? "bg-emerald/5" : ""}>
                  <td className="px-5 py-5 font-mono text-xs text-muted-foreground">{g.gen}</td>
                  <td className="px-5 py-5">
                    <p className="font-semibold text-foreground">{g.name}</p>
                    <p className="text-xs text-muted-foreground">{g.brand}</p>
                  </td>
                  <td className="px-5 py-5 text-foreground">{g.type}</td>
                  <td className="px-5 py-5">
                    <div className="flex items-center gap-3">
                      <span
                        className={`font-semibold ${i === 2 ? "text-emerald" : "text-foreground"}`}
                      >
                        {g.loss}
                      </span>
                      <div className="h-1.5 w-32 overflow-hidden rounded-full bg-muted">
                        <div
                          className={`h-full ${i === 2 ? "bg-emerald" : "bg-indigo/60"}`}
                          style={{ width: `${g.lossValue}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-5">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ${
                        g.statusType === "approved" ? "bg-emerald/10 text-emerald" : "bg-indigo/15 text-indigo"
                      }`}
                    >
                      <span
                        className={`h-1.5 w-1.5 rounded-full ${g.statusType === "approved" ? "bg-emerald" : "bg-indigo"}`}
                      />
                      {g.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-6 grid gap-4 md:hidden">
          {generations.map((g, i) => (
            <div key={g.name} className="card-elevated p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                    {g.gen}
                  </p>
                  <p className="mt-1 text-lg font-semibold text-foreground">{g.name}</p>
                  <p className="text-xs text-muted-foreground">{g.brand}</p>
                </div>
                <span
                  className={`font-display text-3xl ${i === 2 ? "text-emerald" : "text-foreground"}`}
                >
                  {g.loss}
                </span>
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-muted">
                <div
                  className={`h-full ${i === 2 ? "bg-emerald" : "bg-indigo/60"}`}
                  style={{ width: `${g.lossValue}%` }}
                />
              </div>
              <div className="mt-3 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{g.type}</span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 font-medium ${
                    g.statusType === "approved" ? "bg-emerald/10 text-emerald" : "bg-indigo/15 text-indigo"
                  }`}
                >
                  {g.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      <ConsultCta variant="section" />
      <RelatedLinks only={["/vergleich", "/wissenschaft", "/zulassung"]} />
    </div>
  );
}
